import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useViewport } from '../../hooks/useViewport';
import Logo from '../../components/common/Logo';
import { UI, ui, buttonStyle, fieldStyles } from '../../utils/ui';

const ForgotPassword = () => {
  const { isDesktop } = useViewport();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setMessage('Password reset link has been sent to your email');
      setLoading(false);
    }, 1500);
  };

  const benefits = [
    'Easy online booking',
    'Breathtaking Himalayan views',
    'Guided trekking experiences',
    'Cozy bonfire evenings',
  ];

  const spinner = (
    <span
      style={{
        width: 16,
        height: 16,
        border: '2px solid rgba(255,255,255,0.35)',
        borderTopColor: '#ffffff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }}
    />
  );

  return (
    <div style={{ ...ui.page, display: 'flex' }}>
      {/* Left — Branding */}
      {isDesktop && (
        <div
          style={{
            width: '41.67%',
            position: 'relative',
            backgroundImage: 'url(https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=1200&h=1600&fit=crop)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(22,163,74,0.92) 0%, rgba(21,128,61,0.96) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ textAlign: 'center', color: '#ffffff', padding: '0 40px', maxWidth: 460 }}>
              <div
                style={{
                  display: 'inline-flex',
                  margin: '0 auto 24px',
                  animation: 'float 4s ease-in-out infinite',
                }}
              >
                <Logo height={104} onDark showText={false} style={{ padding: '16px 22px', borderRadius: 20 }} />
              </div>
              <h1 style={{ fontFamily: UI.font.heading, fontSize: 46, fontWeight: 900, margin: '0 0 8px' }}>
                Reset Password
              </h1>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.82)', textTransform: 'uppercase', letterSpacing: 4, fontSize: 12 }}>
                We&apos;ve got you covered
              </p>
              <div style={{ width: 64, height: 1, backgroundColor: 'rgba(255,255,255,0.35)', margin: '28px auto' }} />
              <p style={{ margin: '0 0 28px', color: 'rgba(255,255,255,0.86)', fontSize: 16, lineHeight: 1.7 }}>
                No worries. Enter your email and we&apos;ll send you a reset link.
              </p>
              <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {benefits.map((b) => (
                  <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <CheckCircle2 size={18} color="rgba(255,255,255,0.9)" />
                    <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13 }}>{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right — Form */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ width: '100%', maxWidth: 460 }}>
          {/* Mobile Logo */}
          {!isDesktop && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 24 }}>
              <Logo height={48} />
            </div>
          )}

          <div style={{ ...ui.card, borderRadius: 20, boxShadow: '0 25px 50px rgba(0,0,0,0.12)' }}>
            <div style={{ padding: 32 }}>
              <Link
                to="/login"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  color: UI.colors.muted,
                  textDecoration: 'none',
                  marginBottom: 18,
                }}
              >
                <ArrowLeft size={16} /> Back to Login
              </Link>

              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 9999,
                    backgroundColor: 'rgba(22,163,74,0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                  }}
                >
                  <Mail size={36} color={UI.colors.primary} />
                </div>
                <h2 style={{ fontFamily: UI.font.heading, fontSize: 28, fontWeight: 900, color: UI.colors.text, margin: 0 }}>Forgot Password?</h2>
                <p style={{ color: UI.colors.muted, margin: '10px auto 0', fontSize: 14, lineHeight: 1.6, maxWidth: 360 }}>
                  Enter the email associated with your account and we&apos;ll send a reset link.
                </p>
              </div>

              {message ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ padding: 14, borderRadius: 14, backgroundColor: '#f0fdf4', border: '1px solid #86efac', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <CheckCircle2 size={20} color={UI.colors.primary} style={{ marginTop: 2 }} />
                    <div>
                      <p style={{ margin: 0, fontWeight: 800, fontSize: 13, color: '#166534' }}>Email Sent!</p>
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#166534', opacity: 0.9 }}>{message}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMessage('')}
                    style={{ ...buttonStyle('outline'), width: '100%', padding: '12px 16px', borderRadius: 14 }}
                  >
                    Resend Email
                  </button>
                  <Link to="/login" style={{ ...buttonStyle('primary'), width: '100%', padding: '12px 16px', borderRadius: 14 }}>
                    Back to Login
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div>
                    <label style={fieldStyles.label}>Email Address</label>
                    <div className="input-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 14, border: `1.5px solid ${UI.colors.border}`, backgroundColor: '#ffffff' }}>
                      <Mail size={16} color="#9ca3af" />
                      <input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        style={{ ...fieldStyles.input, border: 'none', padding: 0, borderRadius: 0, backgroundColor: 'transparent', flex: 1 }}
                      />
                    </div>
                    <div style={fieldStyles.help}>We&apos;ll email you a secure reset link.</div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      ...buttonStyle('primary'),
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: 14,
                      opacity: loading ? 0.75 : 1,
                    }}
                  >
                    {loading ? spinner : null}
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </form>
              )}

              <div style={{ marginTop: 18, padding: 14, backgroundColor: '#f3f4f6', borderRadius: 14, border: `1px solid ${UI.colors.border}`, textAlign: 'center' }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: UI.colors.text }}>Still need help?</p>
                <a href="mailto:support@buranshikes.com" style={{ display: 'inline-block', marginTop: 6, fontSize: 13, color: UI.colors.primary, fontWeight: 800, textDecoration: 'none' }}>
                  support@buranshikes.com
                </a>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', marginTop: 16 }}>
                <Link to="/register" style={{ fontSize: 13, color: UI.colors.muted, textDecoration: 'none', fontWeight: 700 }}>
                  Create account
                </Link>
                <span style={{ color: '#d1d5db' }}>•</span>
                <Link to="/" style={{ fontSize: 13, color: UI.colors.muted, textDecoration: 'none', fontWeight: 700 }}>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;

